// src/admin/AdminBlogs.jsx
import { useEffect, useRef, useState } from "react";
import {
  collection, getDocs, addDoc, deleteDoc,
  doc, updateDoc, serverTimestamp, orderBy, query,
} from "../services/mysqlStore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, storage } from "../services/mysqlStorage";
import { db } from "../services/mysqlStore";

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "",
  coverImageUrl: "", author: "Circuit Printing Team", published: true,
};

function slugify(text) {
  return text.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

// ── Reusable image uploader
function ImageUploader({ value, onChange, folder = "blogs" }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    setUploading(true);
    task.on(
      "state_changed",
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => { console.error(err); setUploading(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        onChange(url);
        setUploading(false);
        setProgress(0);
      }
    );
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    if (value) { try { await deleteObject(ref(storage, value)); } catch {} }
    onChange("");
  };

  return (
    <div
      className={`adm-upload-area ${drag ? "dragover" : ""}`}
      onClick={() => !uploading && inputRef.current.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
    >
      <input ref={inputRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files[0])} />
      {value ? (
        <>
          <img src={value} alt="preview" className="adm-upload-preview" />
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>Click to replace</p>
          <button className="adm-upload-remove" onClick={handleRemove} title="Remove">×</button>
        </>
      ) : (
        <div className="adm-upload-label">
          <span className="icon">🖼️</span>
          <strong>Click to upload or drag & drop</strong>
          <span>Recommended: 1200×630px (cover image)</span>
        </div>
      )}
      {uploading && (
        <>
          <p style={{ color: "var(--cyan)", fontSize: 13, marginTop: 8 }}>Uploading... {progress}%</p>
          <div className="adm-upload-progress">
            <div className="adm-upload-progress-bar" style={{ width: `${progress}%` }} />
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminBlogs() {
  const [blogs, setBlogs]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "blogs"), orderBy("createdAt", "desc")));
      setBlogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBlogs(); }, []);

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
    setSlugTouched(false);
    setMsg("");
  };

  const handleEdit = (b) => {
    setEditingId(b.id);
    setForm({
      title: b.title || "", slug: b.slug || "", excerpt: b.excerpt || "",
      content: b.content || "", coverImageUrl: b.coverImageUrl || "",
      author: b.author || "Circuit Printing Team", published: b.published !== false,
    });
    setSlugTouched(true);
    setMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTitleChange = (val) => {
    setForm(f => ({ ...f, title: val, slug: slugTouched ? f.slug : slugify(val) }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setMsg("error:Title is required"); return; }
    if (!form.slug.trim()) { setMsg("error:URL slug is required"); return; }
    setSaving(true);
    setMsg("");
    try {
      const data = {
        title: form.title.trim(),
        slug: slugify(form.slug),
        excerpt: form.excerpt.trim(),
        content: form.content,
        coverImageUrl: form.coverImageUrl,
        author: form.author.trim() || "Circuit Printing Team",
        published: form.published,
      };
      if (editingId) {
        await updateDoc(doc(db, "blogs", editingId), data);
        setMsg("success:Blog updated!");
      } else {
        await addDoc(collection(db, "blogs"), { ...data, createdAt: serverTimestamp() });
        setMsg("success:Blog published!");
      }
      resetForm();
      fetchBlogs();
    } catch (e) {
      console.error(e);
      setMsg("error:Failed to save. Slug may already be in use.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, coverImageUrl) => {
    if (!window.confirm("Delete this blog post?")) return;
    if (coverImageUrl) { try { await deleteObject(ref(storage, coverImageUrl)); } catch {} }
    await deleteDoc(doc(db, "blogs", id));
    fetchBlogs();
  };

  const togglePublished = async (b) => {
    try {
      await updateDoc(doc(db, "blogs", b.id), { published: !b.published });
      setBlogs(prev => prev.map(x => x.id === b.id ? { ...x, published: !b.published } : x));
    } catch (e) { console.error(e); }
  };

  const isErr = msg.startsWith("error:");
  const msgText = msg.replace(/^(success|error):/, "");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* Form */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">{editingId ? "Edit Blog Post" : "Write New Blog Post"}</span>
        </div>
        <div style={{ padding: 22 }}>
          <div className="adm-form">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div className="adm-form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Title *</label>
                <input
                  value={form.title}
                  onChange={e => handleTitleChange(e.target.value)}
                  placeholder="5 Tips for Choosing the Right Business Card Stock"
                />
              </div>
              <div className="adm-form-group">
                <label>URL Slug *</label>
                <input
                  value={form.slug}
                  onChange={e => { setSlugTouched(true); setForm(f => ({ ...f, slug: e.target.value })); }}
                  placeholder="5-tips-business-card-stock"
                />
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>
                  /blogs/{form.slug || "your-slug"}
                </span>
              </div>
              <div className="adm-form-group">
                <label>Author</label>
                <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} />
              </div>
              <div className="adm-form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Excerpt <span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400 }}>(short summary shown on blog list)</span></label>
                <textarea
                  value={form.excerpt}
                  onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                  placeholder="A short 1-2 sentence summary..."
                  rows={2}
                  style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", padding: "10px 12px", fontSize: 13, fontFamily: "var(--font-main)", resize: "vertical", outline: "none", lineHeight: 1.6, boxSizing: "border-box" }}
                />
              </div>
              <div className="adm-form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Content <span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400 }}>(supports paragraphs)</span></label>
                <textarea
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="Write your blog content here..."
                  rows={10}
                  style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", padding: "10px 12px", fontSize: 13, fontFamily: "var(--font-main)", resize: "vertical", outline: "none", lineHeight: 1.7, boxSizing: "border-box" }}
                />
              </div>
            </div>

            <div className="adm-form-group">
              <label>Cover Image</label>
              <ImageUploader
                value={form.coverImageUrl}
                onChange={(url) => setForm(f => ({ ...f, coverImageUrl: url }))}
                folder="blogs"
              />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <div
                onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                style={{
                  width: 42, height: 24, borderRadius: 12,
                  background: form.published ? "var(--cyan)" : "rgba(255,255,255,0.15)",
                  position: "relative", transition: "background 0.2s", cursor: "pointer", flexShrink: 0,
                }}
              >
                <span style={{ position: "absolute", top: 2, left: form.published ? 20 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
              </div>
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
                {form.published ? "Published — visible to all visitors" : "Draft — hidden from public"}
              </span>
            </label>

            {msg && (
              <div style={{ padding: "8px 14px", borderRadius: 6, fontSize: 13, background: isErr ? "rgba(239,68,68,0.12)" : "rgba(0,170,221,0.12)", color: isErr ? "#f87171" : "var(--cyan)", border: `1px solid ${isErr ? "#f87171" : "var(--cyan)"}` }}>
                {msgText}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button className="adm-btn adm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : editingId ? "Update Post" : "+ Publish Post"}
              </button>
              {editingId && (
                <button className="adm-btn" onClick={resetForm} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">All Blog Posts ({blogs.length})</span>
        </div>
        {loading ? (
          <p style={{ padding: 24, color: "rgba(255,255,255,0.3)" }}>Loading...</p>
        ) : blogs.length === 0 ? (
          <p style={{ padding: 24, color: "rgba(255,255,255,0.3)" }}>No blog posts yet. Write your first one above.</p>
        ) : (
          blogs.map(b => (
            <div key={b.id} style={{ display: "flex", gap: 14, alignItems: "center", padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ width: 64, height: 48, borderRadius: 8, background: "rgba(255,255,255,0.06)", flexShrink: 0, overflow: "hidden" }}>
                {b.coverImageUrl
                  ? <img src={b.coverImageUrl} alt={b.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.2)", fontSize: 20 }}>📝</div>
                }
              </div>
              <div style={{ flex: 1 }}>
                <strong style={{ color: "#fff", fontSize: 14 }}>{b.title}</strong>
                <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontFamily: "monospace" }}>/blogs/{b.slug}</span>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>by {b.author}</span>
                </div>
              </div>
              <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer", flexShrink: 0 }}>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>{b.published !== false ? "Published" : "Draft"}</span>
                <div onClick={() => togglePublished(b)} style={{ width: 36, height: 20, borderRadius: 10, background: b.published !== false ? "var(--cyan)" : "rgba(255,255,255,0.15)", position: "relative", cursor: "pointer", transition: "background 0.2s" }}>
                  <span style={{ position: "absolute", top: 2, left: b.published !== false ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                </div>
              </label>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button className="adm-btn adm-btn-sm" onClick={() => handleEdit(b)} style={{ background: "rgba(0,170,221,0.12)", border: "1px solid var(--cyan)", color: "var(--cyan)" }}>Edit</button>
                <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => handleDelete(b.id, b.coverImageUrl)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
